import React, { useEffect, useRef } from "react";

const ResumeAnimation = () => {
  const skillBarsRef = useRef(null);

  useEffect(() => {
    if (!skillBarsRef.current) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.querySelectorAll(".bar-fill").forEach((f) => f.classList.add("animated"));
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(skillBarsRef.current);
    if (prefersReduced) {
      skillBarsRef.current.querySelectorAll(".bar-fill").forEach((f) => f.classList.add("animated"));
    }
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ── Services ── */}
      <section className="d2-services" id="services" aria-labelledby="services-title">
        <div className="d2-container">
          <span className="section-label reveal">What I do</span>
          <h2 className="section-title reveal reveal-delay-1" id="services-title">Services</h2>
          <div className="services-grid">
            <div className="service-card reveal">
              <div className="service-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" />
                </svg>
              </div>
              <div className="service-name">Android Development</div>
              <p className="service-desc">Native Android apps built with Kotlin and Jetpack Compose. Clean architecture, scalable codebases, and smooth 60fps experiences.</p>
            </div>
            <div className="service-card reveal reveal-delay-1">
              <div className="service-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <div className="service-name">Flutter Development</div>
              <p className="service-desc">Cross-platform Flutter apps for Android and iOS from a single codebase, without compromising on native performance or feel.</p>
            </div>
            <div className="service-card reveal reveal-delay-2">
              <div className="service-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" />
                </svg>
              </div>
              <div className="service-name">Website Development</div>
              <p className="service-desc">Modern, performant websites and web apps built with React. Responsive designs, clean code, and seamless user experiences across all devices.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Skills ── */}
      <section className="d2-skills" id="skills" aria-labelledby="skills-title">
        <div className="d2-container">
          <div className="skills-layout">
            <div>
              <span className="section-label reveal">Expertise</span>
              <h2 className="section-title reveal reveal-delay-1" id="skills-title">My Skills</h2>
              <p className="section-desc reveal reveal-delay-2">Technologies I use to build production-grade mobile and web applications.</p>

              <div className="skills-group reveal reveal-delay-2" style={{ marginTop: "2rem" }}>
                <div className="skills-group-title">Mobile</div>
                <div className="skill-chips">
                  <span className="chip"><span className="chip-dot" style={{ background: "#7F52FF" }} />Kotlin</span>
                  <span className="chip"><span className="chip-dot" style={{ background: "#54C5F8" }} />Flutter / Dart</span>
                  <span className="chip"><span className="chip-dot" style={{ background: "#4285F4" }} />Jetpack Compose</span>
                  <span className="chip">Room</span>
                  <span className="chip">WorkManager</span>
                  <span className="chip">DataStore</span>
                  <span className="chip">Paging 3</span>
                </div>
              </div>
              <div className="skills-group reveal reveal-delay-3">
                <div className="skills-group-title">Architecture</div>
                <div className="skill-chips">
                  <span className="chip">Clean Architecture</span>
                  <span className="chip">MVVM / MVI</span>
                  <span className="chip">Hilt / DI</span>
                  <span className="chip">Room</span>
                  <span className="chip">Coroutines</span>
                  <span className="chip">Kotlin Flow</span>
                </div>
              </div>
              <div className="skills-group reveal reveal-delay-4">
                <div className="skills-group-title">Web &amp; Tools</div>
                <div className="skill-chips">
                  <span className="chip">TypeScript</span>
                  <span className="chip">Git</span>
                  <span className="chip">Firebase</span>
                  <span className="chip">AI-driven Dev</span>
                </div>
              </div>
            </div>

            <div>
              <div className="skills-group-title reveal" style={{ marginBottom: "1.5rem" }}>Proficiency</div>
              <div className="bar-list" id="skill-bars" ref={skillBarsRef}>
                <div className="bar-item reveal">
                  <div className="bar-meta"><span className="bar-name">Android</span><span className="bar-pct">92%</span></div>
                  <div className="bar-track"><div className="bar-fill" style={{ "--target-width": "92%" }} /></div>
                </div>
                <div className="bar-item reveal reveal-delay-1">
                  <div className="bar-meta"><span className="bar-name">AI Driven Development</span><span className="bar-pct">80%</span></div>
                  <div className="bar-track"><div className="bar-fill" style={{ "--target-width": "80%" }} /></div>
                </div>
                <div className="bar-item reveal reveal-delay-2">
                  <div className="bar-meta"><span className="bar-name">Flutter</span><span className="bar-pct">75%</span></div>
                  <div className="bar-track"><div className="bar-fill" style={{ "--target-width": "75%" }} /></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Experience ── */}
      <section className="d2-experience" id="experience" aria-labelledby="exp-title">
        <div className="d2-container">
          <span className="section-label reveal">Career</span>
          <h2 className="section-title reveal reveal-delay-1" id="exp-title">Experience</h2>
          <div className="exp-layout">
            <div className="exp-line" aria-hidden="true" />

            {/* Porter */}
            <div className="exp-item reveal">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">Porter</span>
                  <span className="exp-type">Full-Time</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header" style={{ borderBottom: "none", paddingBottom: 0 }}>
                      <span className="exp-role-title">SDE-4 Android</span>
                      <span className="exp-date">Sept 2024 – Present</span>
                    </div>
                  </div>
                  <div style={{ marginTop: "-0.875rem" }}>
                    <div className="exp-role-header">
                      <span className="exp-role-title">SDE-3 Android</span>
                      <span className="exp-date">Sept 2022 – Sept 2024</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Lead a team of 5 engineers driving backend architecture and delivery.</li>
                      <li>Designed a vehicle cataloging platform powering vehicle rollout and partner onboarding.</li>
                      <li>Architected real-time driver tracking services handling ~1.5K requests/sec.</li>
                      <li>Led system design discussions, conducted technical interviews, and mentored engineers.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Godutch */}
            <div className="exp-item reveal reveal-delay-1">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">Godutch Technologies Pvt. Ltd.</span>
                  <span className="exp-type">Full-Time Remote</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header">
                      <span className="exp-role-title">Sr. Software Developer</span>
                      <span className="exp-date">Jan 2022 – Sept 2022</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Participated in the development of GoDutch Android Application.</li>
                      <li>Led the development of a new Flutter project from scratch.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* YourStory */}
            <div className="exp-item reveal reveal-delay-2">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">YourStory Media Pvt. Ltd.</span>
                  <span className="exp-type">Full-Time Remote</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header">
                      <span className="exp-role-title">Sr. Software Developer</span>
                      <span className="exp-date">Feb 2020 – Jan 2022</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Designed and developed YourStory Native Android Application.</li>
                      <li>Designed and developed Flutter Application from scratch.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* CrimzonCode */}
            <div className="exp-item reveal reveal-delay-3">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">CrimzonCode</span>
                  <span className="exp-type">Full-Time</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header">
                      <span className="exp-role-title">Software Developer — Android</span>
                      <span className="exp-date">Feb 2018 – Feb 2020</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Managed and developed the TopQuiz, BattleKing and MobileBaazi Quiz Gaming Applications.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Paperwork */}
            <div className="exp-item reveal reveal-delay-4">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">Paperwork Consulting Pvt. Ltd.</span>
                  <span className="exp-type">Full-Time</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header">
                      <span className="exp-role-title">Head of Product Development</span>
                      <span className="exp-date">Sep 2017 – Dec 2017</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Developed the BingeTales Book-reading Application.</li>
                      <li>BingeTales — first of its kind, organising content across Horror, Teen, Mythology, Humour, Mystery, Children, and Classics.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* ClinMD */}
            <div className="exp-item reveal">
              <div className="exp-dot" aria-hidden="true" />
              <div className="exp-card">
                <div className="exp-header">
                  <span className="exp-company">ClinMD</span>
                  <span className="exp-type">Internship</span>
                </div>
                <div className="exp-roles">
                  <div>
                    <div className="exp-role-header">
                      <span className="exp-role-title">Android Developer Intern</span>
                      <span className="exp-date">July 2017 – Sept 2017</span>
                    </div>
                    <ul className="exp-bullets">
                      <li>Developed the Prescription writing Application and interfaced it with Smart pen using Bluetooth.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Education ── */}
      <section className="d2-education" id="education" aria-labelledby="edu-title">
        <div className="d2-container">
          <span className="section-label reveal">Academic</span>
          <h2 className="section-title reveal reveal-delay-1" id="edu-title">Education</h2>
          <div className="edu-list">
            <div className="edu-card reveal reveal-delay-1">
              <div className="edu-year">2017</div>
              <div className="edu-divider" aria-hidden="true" />
              <div>
                <div className="edu-degree">B.Tech Computer Science</div>
                <div className="edu-institution">Amity University, Jaipur</div>
              </div>
            </div>
            <div className="edu-card reveal reveal-delay-2">
              <div className="edu-year">2013</div>
              <div className="edu-divider" aria-hidden="true" />
              <div>
                <div className="edu-degree">CBSE Class XII</div>
                <div className="edu-institution">Maheshwari Public School, Jawahar Nagar, Jaipur</div>
              </div>
            </div>
            <div className="edu-card reveal reveal-delay-3">
              <div className="edu-year">2011</div>
              <div className="edu-divider" aria-hidden="true" />
              <div>
                <div className="edu-degree">CBSE Class X</div>
                <div className="edu-institution">Maheshwari Public School, Jawahar Nagar, Jaipur</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ResumeAnimation;
